import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import {
  Key, Check, Pause, Play, AlertTriangle, MessageSquare, RotateCcw,
} from "lucide-react";
import { STAGE_COLORS, STAGE_LABELS, KEY_STATUSES, BED_SIZES } from "../lib/stages";
import { StageBadge, FlagPip } from "./StageBadge";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import PhotoUploader from "./PhotoUploader";
import ResponsiveDialog from "./ResponsiveDialog";
import { useFileUrl } from "../lib/files";
import { canAccessStage } from "../lib/permissions";
import {useApplication} from "../context/ApplicationContext";
import { AssignmentBadge, AssignmentPicker } from "./Assignment";
import { SubmissionSummary } from "./OperationalUI";
import { friendlyStatus } from "../lib/presentation";
import { repairLocation } from "../lib/location";
import OperationalActionMenu from "./OperationalActionMenu";
import {newActivityInstance,recordActivity} from "../lib/activity";

export function projectBuiltInQuestion(questions,id,fallback) {
  return (questions||[]).find(question=>question.id===id)||{id,label:fallback,help_text:"",enabled:true,required:false};
}

export default function RoomDrawer({ roomId, onClose, initialTab = "workflow", focusStage, lockedStage, closeOnStageAction = false }) {
  const { user } = useAuth();
  const {effectiveWorkflow}=useApplication();
  const effectiveStageLabels=Object.fromEntries((effectiveWorkflow?.stages||[]).map(stage=>[stage.key,stage.display_name]));
  const [room, setRoom] = useState(null);
  const [audit, setAudit] = useState([]);
  const [operationalOptions,setOperationalOptions]=useState({flags:[],key_return_options:[]});
  const [tab, setTab] = useState(initialTab);

  const load = useCallback(async () => {
    const [r, a, options] = await Promise.all([
      api.get(`/rooms/${roomId}`),
      api.get(`/rooms/${roomId}/audit`, { params: { limit: 5 } }),
      api.get(`/rooms/${roomId}/operational-options`).catch(()=>({data:{flags:[],key_return_options:[]}})),
    ]);
    setRoom(r.data);
    setAudit(a.data);
    setOperationalOptions(options.data);
  }, [roomId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(()=>{if(roomId)recordActivity({event_type:"modal.opened",module:"tar",entity_type:"room",entity_id:roomId,room_id:roomId},newActivityInstance())},[roomId]);

  if (!room)
    return (
      <ResponsiveDialog title="Room" onClose={onClose}><div className="p-8">Loading…</div></ResponsiveDialog>
    );

  const isCommon = room.type === "common";
  const canUpdateRoomMetadata = (user?.permission_keys || []).includes("config.locations.update");
  const canAdminStaff = (user?.permission_keys || []).includes("tar.rooms.update");
  const canConfiguredFlags=(user?.permission_keys||[]).some(key=>key==="tar.flags.apply"||key==="tar.flags.remove");
  const canUseStage = (stage) => canAccessStage(user, stage);
  const roomOccupied = room.flags?.occupied_override ?? (room.key_status === "Room Occupied");
  const availableTabs = lockedStage
    ? [initialTab]
    : [
        canUseStage("key_return") || Object.keys(room.stages || {}).some((stage) => canUseStage(stage)) ? "workflow" : null,
        canUseStage("checkout_inspection") || canUseStage("maintenance_inspection") ? "inspections" : null,
        canUseStage("internal_repairs") || canUseStage("external_repairs") ? "repairs" : null,
        canUseStage("painting") ? "painting" : null,
        "gallery",
        "audit",
      ].filter(Boolean);
  const activeTab = availableTabs.includes(tab) ? tab : availableTabs[0];

  const setKey = async (v) => {
    try {
      await api.post(`/rooms/${room.id}/key-status`, { key_status: v });
      toast.success(`Key status: ${v}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const setFlag = async (name, val) => {
    try {
      await api.post(`/rooms/${room.id}/flags`, { [name]: val });
      toast.success(`${name} ${val ? "ON" : "OFF"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const setConfiguredFlag=async(flagId,val)=>{try{await api.post(`/rooms/${room.id}/configured-flags/${val?"apply":"remove"}`,{flag_id:flagId});toast.success(`Flag ${val?"applied":"removed"}`);load()}catch(e){toast.error(e.response?.data?.detail||"Flag update failed")}};
  const setKeyOutcome=async outcomeId=>{try{const {data}=await api.post(`/rooms/${room.id}/key-return-outcome`,{outcome_id:outcomeId});toast.success(`Key Return: ${data.outcome.label}`);load()}catch(e){toast.error(e.response?.data?.detail||"Key Return failed")}};

  const stageAction = async (stage, action, extra = {}) => {
    try {
      if(action==="undo_submission") await api.post(`/rooms/${room.id}/stages/${stage}/undo-submission`,{reason:extra.reason});
      else await api.post(`/rooms/${room.id}/stages/${stage}/action`, { action, ...extra });
      toast.success(`${STAGE_LABELS[stage] || stage}: ${action}`);
      if (closeOnStageAction && ["complete", "fail", "hold", "defer_to_cleaner", "skip"].includes(action)) {
        onClose();
      } else {
        load();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const location = repairLocation({ ...room, room_name: room.name });
  return (
    <ResponsiveDialog title={location.primary} subtitle={location.secondary} onClose={onClose} headerActions={<QueryButton roomId={room.id} />} className="sm:max-w-5xl" labelledBy="room-dialog-title">
      <div className="bg-white" data-testid="room-drawer">
        <div className="bg-slate-50 border-b border-gray-200 px-4 lg:px-6 py-3 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="font-chivo font-bold text-lg flex items-center gap-2 flex-wrap" data-testid="drawer-room-summary">
              <span>{room.room_type_name || (room.type === "common" ? "Common area" : "Bedroom")}</span>
              {room.overstayer && (
                <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold uppercase tracking-wider border border-red-300 bg-red-50 text-red-700 px-1.5 py-0.5" data-testid="overstayer-badge" title="Unauthorised Overstayer">
                  <AlertTriangle size={10} /> Overstayer
                </span>
              )}
              {room.type === "common" && room.flat_has_summer_stay && (
                <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold uppercase tracking-wider border border-sky-300 bg-sky-50 text-sky-800 px-1.5 py-0.5" data-testid="common-summer-stay-badge">
                  <AlertTriangle size={10} /> Summer stay in flat
                </span>
              )}
            </div>
            <div className="flex gap-2 mt-2 flex-wrap">
              <StageBadge stage={room.current_stage} />
              {room.flags?.rebooking && <FlagPip kind="rebooking" />}
              {room.flags?.storage && <FlagPip kind="storage" />}
              {room.flags?.summer_stay && <FlagPip kind="summer_stay" />}
              {room.flags?.refurbishment && <FlagPip kind="refurbishment" />}
              {canAdminStaff ? <button onClick={() => setFlag("occupied_override", !roomOccupied)} aria-label={roomOccupied ? "Remove Occupied Room flag" : "Add Occupied Room flag"} className={`rounded-lg border px-2 py-1 ${roomOccupied ? "border-red-300 bg-red-50" : "border-dashed border-slate-300 text-slate-600"}`} data-testid="occupied-override-toggle"><FlagPip kind="occupied" /></button> : roomOccupied && <FlagPip kind="occupied" />}
            </div>
          </div>
        </div>

        <div className="room-tabs px-4 lg:px-6 pt-2 border-b border-gray-200 flex gap-2 overflow-x-auto" role="tablist" aria-label="Room details">
          {availableTabs.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`drawer-tab-${t}`}
              role="tab"
              aria-selected={activeTab === t}
              className={`pb-3 text-xs font-semibold uppercase tracking-wider transition-colors ${
                activeTab === t ? "text-black border-b-2 border-black" : "text-gray-500 hover:text-black"
              }`}
            >
              {t}{lockedStage ? " (locked)" : ""}
            </button>
          ))}
        </div>

        <div className="p-4 lg:p-6 space-y-6">
          {activeTab === "workflow" && (
            <WorkflowTab
              room={room}
              isCommon={isCommon}
              canAdminStaff={canAdminStaff}
              canUpdateRoomMetadata={canUpdateRoomMetadata}
              setKey={setKey}
              setFlag={setFlag}
              setConfiguredFlag={setConfiguredFlag}
              setKeyOutcome={setKeyOutcome}
              operationalOptions={operationalOptions}
              stageLabels={effectiveStageLabels}
              canConfiguredFlags={canConfiguredFlags}
              stageAction={stageAction}
              onMetaSave={load}
              focusStage={focusStage}
              lockedStage={lockedStage}
              canUseStage={canUseStage}
            />
          )}
          {activeTab === "inspections" && (
            <InspectionsTab room={room} onDone={load} focusStage={focusStage} lockedStage={lockedStage} canUseStage={canUseStage} />
          )}
          {activeTab === "repairs" && <RepairsTab room={room} onDone={load} lockedStage={lockedStage} canUseStage={canUseStage} />}
          {activeTab === "painting" && <PaintingTab room={room} onAction={stageAction} canUseStage={canUseStage}/>}
          {activeTab === "gallery" && <GalleryTab room={room} />}
          {activeTab === "audit" && <AuditTab roomId={room.id} audit={audit} onAudit={setAudit} />}
        </div>
      </div>
    </ResponsiveDialog>
  );
}

function PaintingTab({room,onAction,canUseStage}) {
  const state=room.stages?.painting||{status:"pending"};
  return <section className="rounded-xl border bg-white p-5" data-testid="room-painting-tab"><h3 className="text-lg font-black">Painting work</h3><p className="mt-1 text-sm text-slate-600">Painting is Room-level workflow work, not a Repair record. Manage it here without duplicating repair data.</p>{state.note&&<p className="mt-4 rounded-lg bg-orange-50 p-3 text-sm text-orange-900">{state.note}</p>}<div className="mt-4 flex items-center justify-between gap-3"><StatusPill status={state.status}/><StageActions stage="painting" st={state} room={room} onAction={onAction} canUseStage={canUseStage}/></div></section>;
}

function GalleryTab({ room }) {
  const photos = room.photos_gallery || [];
  return (
    <div className="space-y-3">
      <div className="text-xs font-mono uppercase tracking-wider text-gray-500">Room photo gallery</div>
      {photos.length === 0 ? (
        <div className="border border-gray-200 bg-white p-4 text-sm text-gray-500">No photos uploaded for this room yet.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {photos.map((p) => <PhotoThumb key={`gallery-${p}`} path={p} />)}
        </div>
      )}
    </div>
  );
}

function WorkflowTab({ room, isCommon, canAdminStaff, canUpdateRoomMetadata, setKey, setFlag, setConfiguredFlag, setKeyOutcome, operationalOptions, stageLabels, canConfiguredFlags, stageAction, onMetaSave, focusStage, lockedStage, canUseStage }) {
  const {user}=useAuth();
  const [assignmentStage,setAssignmentStage]=useState(null);
  const [showFlagPicker,setShowFlagPicker]=useState(false);
  const visibleStages = lockedStage
    ? Object.fromEntries(Object.entries(room.stages).filter(([k]) => k === lockedStage && canUseStage(k)))
    : Object.fromEntries(Object.entries(room.stages).filter(([k]) => canUseStage(k)));
  return (
    <>
      {!isCommon && !lockedStage && canUseStage("key_return") && (
        <div className="border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-gray-500 mb-2">
            <Key size={12} /> Keys returned?
          </div>
          {operationalOptions.key_return_options.length>0?<select value={room.key_return_outcome?.id||""} onChange={e=>e.target.value&&setKeyOutcome(e.target.value)} data-testid="key-return-outcome-select" className="min-h-11 w-full border border-gray-300 bg-white px-3 py-2 text-sm"><option value="">Choose outcome…</option>{operationalOptions.key_return_options.map(option=><option key={option.id} value={option.id}>{option.name}</option>)}</select>:<select
            value={room.key_status || "Room Occupied"}
            onChange={(e) => setKey(e.target.value)}
            data-testid="key-status-select"
            className="w-full border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:border-black"
          >
            {KEY_STATUSES.map((k) => (
              <option key={k} value={k}>{k}</option>
            ))}
          </select>}
        </div>
      )}

      {!isCommon && lockedStage === "key_return" && canUseStage("key_return") && (
        <div className="border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-gray-500 mb-2">
            <Key size={12} /> Keys returned?
          </div>
          {operationalOptions.key_return_options.length>0?<select value={room.key_return_outcome?.id||""} onChange={e=>e.target.value&&setKeyOutcome(e.target.value)} data-testid="key-return-outcome-select" className="min-h-11 w-full border border-gray-300 bg-white px-3 py-2 text-sm"><option value="">Choose outcome…</option>{operationalOptions.key_return_options.map(option=><option key={option.id} value={option.id}>{option.name}</option>)}</select>:<select
            value={room.key_status || "Room Occupied"}
            onChange={(e) => setKey(e.target.value)}
            data-testid="key-status-select"
            className="w-full border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:border-black"
          >
            {KEY_STATUSES.map((k) => (
              <option key={k} value={k}>{k}</option>
            ))}
          </select>}
        </div>
      )}

      {!isCommon && !lockedStage && <RoomMetaForm room={room} onSave={onMetaSave} canEdit={canUpdateRoomMetadata} />}

      {!lockedStage && (
        <div className="border border-gray-200 p-4">
          <div className="text-xs font-mono uppercase tracking-wider text-gray-500 mb-3">
            Room flags
          </div>
          <div className="flex gap-2 flex-wrap" data-testid="active-room-flags">
            {operationalOptions.flags.filter(flag=>(room.configured_flags||[]).includes(flag.id)||(flag.legacy_key==="priority"?!!room.priority:!!room.flags?.[flag.legacy_key])).map(flag=><FlagToggle key={flag.id} label={flag.name} on onChange={value=>setConfiguredFlag(flag.id,value)} disabled={!canConfiguredFlags} testId={`configured-flag-${flag.id}`}/>)}
            {canConfiguredFlags&&<button type="button" onClick={()=>setShowFlagPicker(value=>!value)} className="min-h-11 rounded-lg border border-dashed border-slate-400 bg-white px-3 text-sm font-semibold">+ Add flag</button>}
          </div>
          {showFlagPicker&&<div className="mt-3 grid gap-2 rounded-lg border bg-slate-50 p-3 sm:grid-cols-2" data-testid="available-room-flags">{operationalOptions.flags.filter(flag=>flag.status==="active"&&!(room.configured_flags||[]).includes(flag.id)&&!(flag.legacy_key==="priority"?room.priority:room.flags?.[flag.legacy_key])).map(flag=><button type="button" key={flag.id} onClick={()=>setConfiguredFlag(flag.id,true)} className="min-h-11 rounded border bg-white px-3 text-left text-sm"><strong>{flag.name}</strong>{flag.description&&<small className="block text-slate-500">{flag.description}</small>}</button>)}{!operationalOptions.flags.some(flag=>flag.status==="active"&&!(room.configured_flags||[]).includes(flag.id)&&!(flag.legacy_key==="priority"?room.priority:room.flags?.[flag.legacy_key]))&&<p className="text-sm text-slate-500">No more flags are available.</p>}</div>}
          {!canAdminStaff && (
            <div className="text-[10px] font-mono text-gray-500 mt-2">
              Admin/staff role required to change flags.
            </div>
          )}
        </div>
      )}

      <div>
        <div className="text-xs font-mono uppercase tracking-wider text-gray-500 mb-3">
          {lockedStage ? "Stage" : "Stages"}
        </div>
        <div className="border border-gray-200 bg-white">
          {Object.entries(visibleStages).map(([stage, st]) => {
            const c = STAGE_COLORS[stage] || STAGE_COLORS.key_return;
            const focused = focusStage === stage;
            return (
              <div
                key={stage}
                className={`px-4 py-3 border-b border-gray-100 last:border-b-0 flex items-center justify-between gap-4 ${focused ? "bg-yellow-50" : ""}`}
                data-testid={`stage-row-${stage}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-chivo font-black uppercase tracking-wider" style={{ color: c.text }}>
                      {stageLabels[stage]||STAGE_LABELS[stage]||"Workflow stage"}
                    </span>
                    <StatusPill status={st.status} />
                  </div>
                  {st.note && (
                    <div className="text-[11px] font-mono text-gray-500 mt-0.5 truncate">{st.note}</div>
                  )}
                  {st.status==="completed"&&st.form_data&&<div className="mt-3"><SubmissionSummary data={st.form_data} submittedAt={st.completed_at} submittedBy={st.completed_by}/></div>}
                  <div className="mt-2 flex flex-wrap items-center gap-2"><AssignmentBadge assignment={st.assignment}/>{(user?.permission_keys||[]).includes("tar.stages.assign")&&<button type="button" className="text-xs font-semibold text-indigo-700" onClick={()=>setAssignmentStage(assignmentStage===stage?null:stage)}>{st.assignment?"Reassign":"Assign"}</button>}</div>
                  {assignmentStage===stage&&<div className="mt-3"><AssignmentPicker optionsUrl={`/rooms/${room.id}/stages/${stage}/assignment-options`} saveUrl={`/rooms/${room.id}/stages/${stage}/assignment`} assignment={st.assignment}/></div>}
                </div>
                <StageActions stage={stage} st={st} room={room} onAction={stageAction} canUseStage={canUseStage} />
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

function RoomMetaForm({ room, onSave, canEdit }) {
  const [bedSize, setBedSize] = useState(room.bed_size || "");
  const [bathroom, setBathroom] = useState(room.bathroom || "ensuite");
  const [contractEnd, setContractEnd] = useState(room.contract_end_date || "");
  const dirty = bedSize !== (room.bed_size || "") || bathroom !== (room.bathroom || "ensuite") || contractEnd !== (room.contract_end_date || "");
  const save = async () => {
    await api.patch(`/rooms/${room.id}`, {
      bed_size: bedSize || null,
      bathroom,
      contract_end_date: contractEnd || null,
    });
    toast.success("Room updated");
    onSave();
  };
  return (
    <div className="border border-gray-200 p-4">
      <div className="text-xs font-mono uppercase tracking-wider text-gray-500 mb-3">
        Room metadata
      </div>
      {!canEdit ? <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3" data-testid="room-metadata-readonly"><div><dt className="text-xs uppercase text-gray-500">Bed size</dt><dd>{room.bed_size || "Not recorded"}</dd></div><div><dt className="text-xs uppercase text-gray-500">Bathroom</dt><dd>{room.bathroom === "ensuite" ? "Ensuite" : "Shared"}</dd></div><div><dt className="text-xs uppercase text-gray-500">Contract end</dt><dd>{room.contract_end_date || "Not recorded"}</dd></div></dl> : <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-mono uppercase tracking-wider text-gray-500 mb-1">Bed size</label>
          <select
            value={bedSize}
            onChange={(e) => setBedSize(e.target.value)}
            data-testid="room-bed-size-select"
            className="w-full border border-gray-300 px-3 py-2 text-sm bg-white"
          >
            <option value="">—</option>
            {BED_SIZES.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-wider text-gray-500 mb-1">Bathroom type</label>
          <select
            value={bathroom}
            onChange={(e) => setBathroom(e.target.value)}
            data-testid="room-bathroom-select"
            className="w-full border border-gray-300 px-3 py-2 text-sm bg-white"
          >
            <option value="ensuite">Ensuite</option>
            <option value="shared">Shared</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-wider text-gray-500 mb-1">Contract end date</label>
          <input
            type="date"
            value={contractEnd}
            onChange={(e) => setContractEnd(e.target.value)}
            data-testid="room-contract-end-input"
            className="w-full border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
      </div>}
      {canEdit && dirty && (
        <button
          onClick={save}
          data-testid="room-meta-save"
          className="mt-3 bg-black text-white px-3 py-1.5 text-xs font-semibold uppercase tracking-wider"
        >
          Save metadata
        </button>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: ["#e5e7eb", "#374151"],
    in_progress: ["#DBEAFE", "#1D4ED8"],
    on_hold: ["#FEF3C7", "#B45309"],
    completed: ["#DCFCE7", "#15803D"],
    skipped: ["#F3F4F6", "#6B7280"],
  };
  const [bg, fg] = map[status] || map.pending;
  return (
    <span className="text-[9px] font-mono font-bold uppercase tracking-wider px-1.5 py-0.5 border" style={{ backgroundColor: bg, color: fg, borderColor: fg }}>
      {friendlyStatus(status)}
    </span>
  );
}

function StageActions({ stage, st, room, onAction, canUseStage }) {
  const { user } = useAuth();
  const {effectiveWorkflow}=useApplication();
  const definition=effectiveWorkflow?.stages?.find(item=>item.key===stage);
  const [showForm, setShowForm] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const isAdmin = (user?.permission_keys || []).includes("tar.stages.reopen");
  const canReset = (user?.permission_keys || []).includes("tar.stages.reset");
  const canAdminStaff = (user?.permission_keys || []).includes("tar.rooms.update");
  const hasAccess = canUseStage ? canUseStage(stage) : canAccessStage(user, stage);

  const isRepairs = stage === "internal_repairs" || stage === "external_repairs";
  const opensAsForm = stage === "bag_and_bin" || stage === "painting";

  const hasForm = ["bag_and_bin", "painting", "cleaning", "cleaning_inspection", "welcome_packs", "refurbishment", "refurbishment_inspection"].includes(stage)||(definition?.questions||[]).length>0;
  const interactionMode=definition?.interaction_mode||(stage==="key_return"?"outcome_confirm":hasForm?"form":"work");
  const btn = "px-2 py-1 text-[10px] font-mono font-bold uppercase border hover:bg-black hover:text-white";

  if (stage === "completed" || !hasAccess || interactionMode === "outcome_confirm") return null;

  // Admin controls on completed/skipped stages
  if (st.status === "completed" || st.status === "skipped") {
    if (!isAdmin && !canReset) return null;
    return (
      <div className="flex gap-1">
        {(stage==="cleaning"||stage==="maintenance_inspection"||stage.startsWith("custom_"))&&(user?.permission_keys||[]).includes("tar.stage_submissions.undo")&&<button data-testid={`stage-undo-${stage}`} className={btn} onClick={()=>{const reason=prompt("Reason for undoing this submission");if(reason?.trim())onAction(stage,"undo_submission",{reason})}}><RotateCcw size={10} className="inline"/> Undo submission</button>}
        {isAdmin&&<button
          data-testid={`stage-reopen-${stage}`}
          className={btn}
          onClick={() => onAction(stage, "reopen")}
          title="Reopen (admin)"
        >
          <RotateCcw size={10} className="inline" /> Reopen
        </button>}
        {canReset&&<button data-testid={`stage-reset-${stage}`} className={btn} onClick={()=>setConfirmReset(true)}><RotateCcw size={10} className="inline"/> Reset</button>}
        {confirmReset&&<ResetStageDialog label={definition?.display_name||STAGE_LABELS[stage]||stage} onCancel={()=>setConfirmReset(false)} onConfirm={()=>{setConfirmReset(false);onAction(stage,"reset")}}/>}
      </div>
    );
  }

  return (
    <div className="flex gap-1">
      {st.status!=="pending"&&canReset&&<button data-testid={`stage-reset-${stage}`} className={btn} onClick={()=>setConfirmReset(true)}><RotateCcw size={10} className="inline"/> Reset</button>}
      {st.status === "pending" && interactionMode === "form" && (
        <button data-testid={`stage-open-form-${stage}`} className={btn + " bg-black text-white"} onClick={() => setShowForm(true)}>Open form</button>
      )}
      {st.status === "pending" && interactionMode === "work" && isAdmin && (
        <button data-testid={`stage-start-${stage}`} className={btn} onClick={() => onAction(stage, "start")}>
          <Play size={10} className="inline" /> Start
        </button>
      )}
      {st.status === "on_hold" && (
        <button data-testid={`stage-resume-${stage}`} className={btn} onClick={() => onAction(stage, "resume")}>
          Resume
        </button>
      )}
      {st.status === "in_progress" && !isRepairs && (
        <>
          {interactionMode === "work" && stage !== "bag_and_bin" && stage !== "painting" && (
            <button data-testid={`stage-hold-${stage}`} className={btn} onClick={() => {
              const r = prompt("Hold reason");
              if (r) onAction(stage, "hold", { hold_reason: r });
            }}>
              <Pause size={10} className="inline" /> Hold
            </button>
          )}
          {opensAsForm ? (
            <button data-testid={`stage-open-form-${stage}`} className={btn + " bg-black text-white"} onClick={() => setShowForm(true)}>
              Open form
            </button>
          ) : (
            <button data-testid={`stage-complete-${stage}`} className={btn + " bg-black text-white"} onClick={() => {
              if (hasForm) setShowForm(true);
              else onAction(stage, "complete");
            }}>
              <Check size={10} className="inline" /> Done
            </button>
          )}
        </>
      )}
      {st.status === "in_progress" && isRepairs && (
        <span className="text-[10px] font-mono text-gray-500">
          Auto-completes when all repairs done
        </span>
      )}
      {showForm && (
        <StageFormDialog
          stage={stage}
          displayName={definition?.display_name}
          questions={definition?.questions||[]}
          room={room}
          onClose={() => setShowForm(false)}
          canAdminStaff={canAdminStaff}
          onSubmit={(payload) => {
            setShowForm(false);
            onAction(stage, payload.action || "complete", payload);
          }}
        />
      )}
      {confirmReset&&<ResetStageDialog label={definition?.display_name||STAGE_LABELS[stage]||stage} onCancel={()=>setConfirmReset(false)} onConfirm={()=>{setConfirmReset(false);onAction(stage,"reset")}}/>}
    </div>
  );
}

function ResetStageDialog({label,onCancel,onConfirm}) {
  return <ResponsiveDialog title={`Reset ${label}?`} onClose={onCancel} className="sm:max-w-md" footer={<div className="flex justify-end gap-2"><button type="button" onClick={onCancel} className="min-h-11 rounded border px-4 font-semibold">Cancel</button><button type="button" onClick={onConfirm} className="min-h-11 rounded bg-slate-950 px-4 font-semibold text-white">Reset stage</button></div>}><p className="p-5 text-sm text-slate-700">This will return the stage to Not Started. Existing submission and audit history will be retained.</p></ResponsiveDialog>;
}

export function QueryButton({ roomId }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [photoPath, setPhotoPath] = useState(null);
  const submit = async () => {
    if (!text.trim()) {
      toast.error("Please enter a message");
      return;
    }
    try {
      await api.post("/queries", { room_id: roomId, text, photo_path: photoPath });
      toast.success("Query sent");
      setOpen(false);
      setText("");
      setPhotoPath(null);
    } catch (e) {
      toast.error("Failed");
    }
  };
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="raise-query-button"
        className="text-[10px] font-mono font-bold uppercase border border-black px-2 py-1 hover:bg-black hover:text-white whitespace-nowrap inline-flex items-center gap-1"
      >
        <MessageSquare size={11} /> Raise Query
      </button>
      {open && (
        <ResponsiveDialog title="Raise a query" onClose={() => setOpen(false)} className="sm:max-w-md">
            <div className="p-5 space-y-3">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Describe your query — admin/staff will reply in Messages."
                rows={4}
                data-testid="query-text-input"
                className="w-full border border-gray-300 p-2 text-sm"
              />
              <PhotoUploader testId="query-photo" value={photoPath} onChange={setPhotoPath} label="Attach photo (optional)" />
            </div>
            <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-xs font-semibold uppercase tracking-wider border border-gray-300 hover:border-black">Cancel</button>
              <button onClick={submit} data-testid="query-submit" className="bg-black text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider">Send</button>
            </div>
        </ResponsiveDialog>
      )}
    </>
  );
}


function ActiveFlags({ room }) {
  const roomOccupied = room.flags?.occupied_override ?? (room.key_status === "Room Occupied");
  const hasFlags = room.flags?.rebooking || room.flags?.storage || room.flags?.summer_stay || room.flags?.refurbishment || room.priority || roomOccupied || (room.type === "common" && room.flat_has_summer_stay);
  return (
    <div className="flex gap-1.5 flex-wrap items-center" data-testid="form-active-flags">
      {room.flags?.rebooking && <FlagPip kind="rebooking" />}
      {room.flags?.storage && <FlagPip kind="storage" />}
      {room.flags?.summer_stay && <FlagPip kind="summer_stay" />}
      {room.flags?.refurbishment && <FlagPip kind="refurbishment" />}
      {room.priority && (
        <span className="text-[9px] font-mono font-bold uppercase tracking-wider px-1.5 py-0.5 border border-yellow-700 bg-yellow-50 text-yellow-800">
          Priority
        </span>
      )}
      {roomOccupied && (
        <span className="text-[9px] font-mono font-bold uppercase tracking-wider px-1.5 py-0.5 border border-red-700 bg-red-50 text-red-700">
          Room occupied
        </span>
      )}
      {room.type === "common" && room.flat_has_summer_stay && (
        <span className="text-[9px] font-mono font-bold uppercase tracking-wider px-1.5 py-0.5 border border-sky-700 bg-sky-50 text-sky-800" data-testid="form-common-summer-stay-label">
          Summer stay in flat
        </span>
      )}
      {!hasFlags && <span className="text-[10px] font-mono text-gray-500">No active flags</span>}
    </div>
  );
}

function RoomFormHeader({ room, settings }) {
  const roomOccupied = room.flags?.occupied_override ?? (room.key_status === "Room Occupied");
  return (
    <div className="border border-gray-200 bg-gray-50 p-3 space-y-2" data-testid="form-room-context">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500">Room</div>
        <div className="font-chivo font-bold text-base">{room.name}</div>
        <div className="text-[11px] font-mono text-gray-500">{room.block_name || ""} · {room.flat_name || ""}</div>
        <div className="flex flex-wrap gap-1 mt-2">
          {roomOccupied && (
            <span className="text-[9px] font-mono font-bold uppercase tracking-wider px-1.5 py-0.5 border border-red-700 bg-red-50 text-red-700" data-testid="form-occupied-label">Room occupied</span>
          )}
          {room.type === "common" && room.flat_has_summer_stay && (
            <span className="text-[9px] font-mono font-bold uppercase tracking-wider px-1.5 py-0.5 border border-sky-700 bg-sky-50 text-sky-800" data-testid="form-common-summer-stay-context-label">Summer stay in flat</span>
          )}
        </div>
      </div>
      <div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500 mb-1">Active flags</div>
        <ActiveFlags room={room} />
      </div>
      {settings?.room_entry_notice_enabled && settings?.room_entry_notice_text && (
        <div className="border border-amber-300 bg-amber-50 text-amber-900 p-2 text-xs" data-testid="room-entry-notice">
          {settings.room_entry_notice_text}
        </div>
      )}
    </div>
  );
}

function formatCleaningAnswer(value) {
  if (value === "yes") return "Yes";
  if (value === "no") return "No";
  if (value === "na") return "N/A";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return "Not answered";
}

function CleaningChecklistSummary({ room }) {
  const cleaning = room.stages?.cleaning?.form_data || {};
  const rows = [
    ["Shower curtain changed", cleaning.shower_curtain],
    ["Hoover bag changed", cleaning.hoover_bag],
    ["Extractor filter changed", cleaning.extractor_filter],
    room.bag_and_bin_delegated_to_cleaner ? ["Bag & Bin completed", cleaning.bag_bin_done] : null,
    ["Mattress protector placed", cleaning.mattress_protector],
  ].filter(Boolean);

  return (
    <div className="border border-gray-200 bg-gray-50 p-3 space-y-2" data-testid="cleaning-checklist-summary">
      <div className="text-xs font-mono uppercase tracking-wider text-gray-500">Cleaner answers</div>
      <div className="space-y-1">
        {rows.map(([label, value]) => {
          const answer = formatCleaningAnswer(value);
          const missed = value === "no" || value === false;
          return (
            <div key={label} className={`text-xs font-mono flex justify-between gap-3 ${missed ? "text-red-700" : "text-gray-700"}`}>
              <span>{label}</span>
              <span className="font-bold uppercase">{answer}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BathroomCleaningNotice({ room, inspection = false }) {
  const includes = room.common_includes || [];
  const commonHasSharedBathroom = room.type === "common" && includes.includes("Shared Bathroom");
  let title = "Bathroom";
  let message = null;

  if (room.type === "common") {
    if (commonHasSharedBathroom) {
      title = "Shared bathroom with common area";
      message = inspection
        ? "This common area includes the flat's shared bathroom. Inspect the shared bathroom as part of this common-area inspection."
        : "This common area includes the flat's shared bathroom. Clean the shared bathroom with this common area.";
    } else {
      title = "Common area";
      message = inspection
        ? "Inspect the common area only; no bedroom ensuite bathroom is attached to this form."
        : "Clean the common area only; no bedroom ensuite bathroom is attached to this form.";
    }
  } else if (room.bathroom === "ensuite") {
    title = "Ensuite bathroom";
    message = inspection
      ? "This room has an ensuite bathroom. Inspect the ensuite bathroom as part of this room."
      : "This room has an ensuite bathroom. Clean the ensuite bathroom as part of this room.";
  } else if (room.bathroom === "shared") {
    title = "Shared bathroom";
    message = inspection
      ? "This bedroom uses a shared bathroom. Inspect the bathroom on the common area form; do not inspect a bathroom against this bedroom."
      : "The shared bathroom should only be cleaned when the common area for this flat becomes becomes available for cleaning.";
  }

  if (!message) return null;
  return (
    <div className="border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900" data-testid="bathroom-cleaning-notice">
      <div className="font-mono font-bold uppercase tracking-wider mb-1">{title}</div>
      <div>{message}</div>
    </div>
  );
}

export function InspectionFormDialog({ stage, room, onClose, onDone, displayName }) {
  const location=repairLocation({...room,room_name:room.name});
  useEffect(()=>{recordActivity({event_type:"form.opened",module:"tar",entity_type:"room",entity_id:room.id,room_id:room.id,form_id:stage},newActivityInstance())},[room.id,stage]);
  return (
    <ResponsiveDialog title={location.primary} subtitle={location.secondary} onClose={onClose} headerActions={<QueryButton roomId={room.id}/>} className="sm:max-w-2xl">
        <div className="p-5 space-y-4" data-testid={`inspection-form-${stage}`}>
          <div><p className="text-xs font-bold uppercase tracking-wide text-slate-500">{displayName||STAGE_LABELS[stage]||"Inspection"}</p><h3 className="text-lg font-bold">Complete inspection</h3></div>
          <InspectionsTab room={room} onDone={onDone} focusStage={stage} lockedStage={stage} />
        </div>
    </ResponsiveDialog>
  );
}

function ConfiguredQuestion({question,value,onChange}){const common={required:question.required,"aria-label":question.label,className:"mt-1 min-h-11 w-full rounded border border-gray-300 bg-white p-2 text-sm"};return <label className="block text-sm font-semibold">{question.label}{question.help_text&&<span className="block text-xs font-normal text-gray-500">{question.help_text}</span>}{question.type==="long_text"?<textarea {...common} rows={3} value={value||""} onChange={e=>onChange(e.target.value)}/>:question.type==="yes_no"?<select {...common} value={value===true?"yes":value===false?"no":""} onChange={e=>onChange(e.target.value==="yes")}><option value="">Choose…</option><option value="yes">Yes</option><option value="no">No</option></select>:question.type==="single_choice"?<select {...common} value={value||""} onChange={e=>onChange(e.target.value)}><option value="">Choose…</option>{(question.options||[]).map(option=><option key={option}>{option}</option>)}</select>:question.type==="multiple_choice"?<select {...common} multiple value={value||[]} onChange={e=>onChange([...e.target.selectedOptions].map(option=>option.value))}>{(question.options||[]).map(option=><option key={option}>{option}</option>)}</select>:["photo","file"].includes(question.type)?<PhotoUploader value={value||[]} onChange={onChange} testId={`question-${question.id}`} label="" multiple/>:<input {...common} type={question.type==="date"?"date":question.type==="number"||question.type==="currency"?"number":"text"} step={question.type==="currency"?"0.01":undefined} value={value||""} onChange={e=>onChange(["number","currency"].includes(question.type)&&e.target.value!==""?Number(e.target.value):e.target.value)}/>}</label>}

export function StageFormDialog({ stage, room, onClose, onSubmit, canAdminStaff: canAdminStaffProp, questions = [], displayName }) {
  const { user } = useAuth();
  const canAdminStaff = canAdminStaffProp ?? (user?.permission_keys||[]).includes("tar.stages.complete_proxy");
  const [customQuestions, setCustomQuestions] = useState([]);
  const [builtInQuestions,setBuiltInQuestions]=useState([]);
  useEffect(()=>{recordActivity({event_type:"form.opened",module:"tar",entity_type:"room",entity_id:room.id,room_id:room.id,form_id:stage},newActivityInstance())},[room.id,stage]);
  const stageNote = room.stages?.[stage]?.note || "";
  const checkoutBagPhoto = room.checkout_inspection?.bag_and_bin_photo_path || room.stages?.checkout_inspection?.form_data?.bag_and_bin_photo_path;
  const isRefurbishmentRoom = Boolean(room.flags?.refurbishment);
  const initial = useMemo(() => {
    if (stage === "cleaning_inspection" || stage === "refurbishment_inspection") return { result: "pass", notes: "", fail_reason: "", photos: [] };
    if (stage === "bag_and_bin") return { waste_removed: true, note: "" };
    if (stage === "painting" || stage === "refurbishment") return { note: "" };
    if (stage === "welcome_packs")
      return { delivered: true, delivered_on: new Date().toISOString().slice(0, 10), note: "" };
    if (stage === "cleaning")
      return {
        shower_curtain: "yes", // yes | no | na
        hoover_bag: "yes",
        extractor_filter: "yes",
        bag_bin_done: !room.bag_and_bin_delegated_to_cleaner,
        mattress_protector: "yes",
        repairs_to_add: [{ description: "" }],
        note: "",
      };
    return {};
  }, [stage, room]);
  const [form, setForm] = useState(initial);
  const [bagHoldOpen, setBagHoldOpen] = useState(false);
  const [bagHoldReason, setBagHoldReason] = useState("");
  const [paintingHoldOpen, setPaintingHoldOpen] = useState(false);
  const [paintingHoldReason, setPaintingHoldReason] = useState("");
  const [cleaningRepairOpen, setCleaningRepairOpen] = useState(false);
  const location=repairLocation({...room,room_name:room.name});
  useEffect(() => {
    (async () => {
      const [{data},{data:foundations}] = await Promise.all([api.get("/settings"),api.get(`/rooms/${room.id}/effective-foundations`).catch(()=>({data:{stage_questions:{}}}))]);
      setCustomQuestions(questions.length ? questions : (data?.custom_form_questions?.[stage] || []));
      setBuiltInQuestions(foundations?.stage_questions?.[stage]||[]);
    })();
  }, [stage, questions]);

  const builtin=(id,fallback)=>projectBuiltInQuestion(builtInQuestions,id,fallback);
  const builtinEnabled=id=>builtInQuestions.find(question=>question.id===id)?.enabled!==false;
  const questionHelp=id=>{const question=builtInQuestions.find(item=>item.id===id);return question?.help_text?<span className="block text-xs font-normal text-slate-500">{question.help_text}</span>:null};

  const holdBagAndBin = () => {
    if (!bagHoldReason.trim()) {
      toast.error("Hold reason required");
      return;
    }
    onSubmit({ action: "hold", hold_reason: bagHoldReason.trim() });
  };

  const deferBagAndBin = () => {
    onSubmit({ action: "defer_to_cleaner" });
  };

  const holdPainting = () => {
    if (!paintingHoldReason.trim()) {
      toast.error("Hold reason required");
      return;
    }
    onSubmit({ action: "hold", hold_reason: paintingHoldReason.trim() });
  };

  const submit = async () => {
    const valueKeys={"builtin.bag_and_bin.waste_removed":"waste_removed","builtin.bag_and_bin.note":"note","builtin.painting.note":"note","builtin.welcome_packs.delivered":"delivered","builtin.welcome_packs.delivered_on":"delivered_on","builtin.welcome_packs.note":"note","builtin.cleaning_inspection.result":"result","builtin.cleaning_inspection.fail_reason":"fail_reason"};
    const missing=builtInQuestions.filter(question=>question.enabled&&question.required&&!question.conditional_required).filter(question=>form[valueKeys[question.id]]===undefined||form[valueKeys[question.id]]===null||form[valueKeys[question.id]]==="");
    if(missing.length){toast.error(`Complete required field: ${missing[0].label}`);return;}
    let formData = form;
    if (stage === "cleaning") {
      const repairsToAdd = (form.repairs_to_add || []).map((r) => (r.description || "").trim()).filter(Boolean);
      for (const desc of repairsToAdd) {
        await api.post(`/rooms/${room.id}/repairs`, { description: desc, kind: "internal" });
      }
      if (room.bag_and_bin_delegated_to_cleaner) formData = { ...form, bag_bin_done: true };
    }
    if (stage === "cleaning_inspection" || stage === "refurbishment_inspection") {
      if (formData.result === "fail") {
        onSubmit({ action: "fail", fail_reason: formData.fail_reason, form_data: formData, note: formData.notes });
      } else {
        onSubmit({ action: "complete", form_data: formData, note: formData.notes });
      }
    } else {
      onSubmit({ action: "complete", form_data: formData, note: formData.note });
    }
  };

  return (
    <ResponsiveDialog title={location.primary} subtitle={location.secondary} onClose={onClose} headerActions={<QueryButton roomId={room.id}/>} className="sm:max-w-md" footer={<div className="flex justify-end gap-2">{stage === "cleaning" && <button type="button" onClick={() => setCleaningRepairOpen((v) => !v)} data-testid="stage-form-add-cleaning-repair" className="px-4 py-2 text-xs font-semibold uppercase tracking-wider border border-gray-400 bg-white">{cleaningRepairOpen ? "Hide repair" : "Add repair"}</button>}<button onClick={submit} data-testid="stage-form-submit" disabled={(stage === "cleaning_inspection" || stage === "refurbishment_inspection") && form.result === "fail" && !form.fail_reason?.trim()} className="px-4 py-2 text-xs font-semibold uppercase tracking-wider bg-black text-white hover:bg-gray-800 disabled:opacity-40">{stage === "bag_and_bin" || stage === "painting" ? "Complete stage" : "Submit"}</button></div>}>
        <div className="p-5 space-y-3" data-testid={`stage-form-${stage}`}>
          <div><p className="text-xs font-bold uppercase tracking-wide text-slate-500">{displayName||STAGE_LABELS[stage]||"Workflow stage"}</p><h3 className="text-lg font-bold">Complete stage</h3></div>
          {customQuestions.map((q) => typeof q==="string"?<div key={q}><Label>{q}</Label><textarea value={(form.custom_answers||{})[q]||""} onChange={e=>setForm({...form,custom_answers:{...(form.custom_answers||{}),[q]:e.target.value}})} className="w-full border border-gray-300 p-2 text-sm" rows={2}/></div>:<ConfiguredQuestion key={q.id} question={q} value={form[q.id]} onChange={value=>setForm({...form,[q.id]:value})}/>)}
          {stage === "bag_and_bin" && (
            <>
              {room.bag_and_bin_delegated_to_cleaner&&(user?.permission_keys||[]).includes("tar.bag_bin.return_internal")&&<button type="button" onClick={()=>onSubmit({action:"return_internal"})} className="min-h-11 w-full rounded border border-indigo-700 bg-white px-3 text-sm font-semibold text-indigo-700" data-testid="bag-bin-return-internal">Return to internal team</button>}
              {checkoutBagPhoto && (
                <div className="border border-gray-200 bg-gray-50 p-3" data-testid="bag-checkout-photo">
                  <Label>Checkout Bag & Bin photo</Label>
                  <PhotoThumb path={checkoutBagPhoto} />
                </div>
              )}
              <Check2 label={builtin("builtin.bag_and_bin.waste_removed","Waste removed").label} v={form.waste_removed} on={(v) => setForm({ ...form, waste_removed: v })} testId="bag-waste-removed" />
              {builtinEnabled("builtin.bag_and_bin.note")&&<>{questionHelp("builtin.bag_and_bin.note")}<Note label={builtin("builtin.bag_and_bin.note","Completion notes").label} required={builtin("builtin.bag_and_bin.note","Completion notes").required} v={form.note} on={(v) => setForm({ ...form, note: v })} testId="bag-note" /></>}
              <div className="border border-gray-200 bg-gray-50 p-3 space-y-3">
                <div className="text-xs font-mono uppercase tracking-wider text-gray-500">Other Bag & Bin actions</div>
                <div className="flex flex-wrap gap-2">
                  {canAdminStaff && !room.bag_and_bin_delegated_to_cleaner && !isRefurbishmentRoom && (
                    <button type="button" data-testid="stage-form-defer-bagbin-cleaner" onClick={deferBagAndBin} className="px-3 py-2 text-xs font-semibold uppercase tracking-wider border border-gray-400 bg-white">
                      Delegate to Cleaner
                    </button>
                  )}
                  <button type="button" data-testid="stage-form-hold-bag_and_bin" onClick={() => setBagHoldOpen((v) => !v)} className="px-3 py-2 text-xs font-semibold uppercase tracking-wider border border-gray-400 bg-white">
                    Hold
                  </button>
                </div>
                {bagHoldOpen && (
                  <div>
                    <Label>Hold reason</Label>
                    <textarea value={bagHoldReason} onChange={(e) => setBagHoldReason(e.target.value)} className="w-full border border-gray-300 p-2 text-sm bg-white" rows={3} data-testid="stage-form-bagbin-hold-reason" />
                    <button type="button" onClick={holdBagAndBin} className="mt-2 px-3 py-2 text-xs font-semibold uppercase tracking-wider border border-black bg-black text-white">
                      Save hold
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
          {stage === "painting" && (
            <>
              {stageNote && (
                <div className="border border-orange-200 bg-orange-50 p-4 text-orange-900" data-testid="painting-instructions">
                  <div className="text-sm font-mono font-bold uppercase tracking-wider mb-2">Painting instructions</div>
                  <div className="whitespace-pre-wrap text-base leading-relaxed">{stageNote}</div>
                </div>
              )}
              {builtinEnabled("builtin.painting.note")&&<>{questionHelp("builtin.painting.note")}<Note label={builtin("builtin.painting.note","Completion notes").label} required={builtin("builtin.painting.note","Completion notes").required} v={form.note} on={(v) => setForm({ ...form, note: v })} testId="painting-note" /></>}
              <div className="border border-gray-200 bg-gray-50 p-3 space-y-3">
                <div className="text-xs font-mono uppercase tracking-wider text-gray-500">Other Painting actions</div>
                <button type="button" data-testid="stage-form-hold-painting" onClick={() => setPaintingHoldOpen((v) => !v)} className="px-3 py-2 text-xs font-semibold uppercase tracking-wider border border-gray-400 bg-white">
                  Hold
                </button>
                {paintingHoldOpen && (
                  <div>
                    <Label>Hold reason</Label>
                    <textarea value={paintingHoldReason} onChange={(e) => setPaintingHoldReason(e.target.value)} className="w-full border border-gray-300 p-2 text-sm bg-white" rows={3} data-testid="stage-form-painting-hold-reason" />
                    <button type="button" onClick={holdPainting} className="mt-2 px-3 py-2 text-xs font-semibold uppercase tracking-wider border border-black bg-black text-white">
                      Save hold
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
          {stage === "welcome_packs" && (
            <>
              {room.flags?.summer_stay && (
                <div className="border border-sky-300 bg-sky-50 text-sky-900 p-3 text-xs" data-testid="wp-summer-stay-warning">
                  <div className="font-bold uppercase mb-1">Summer Stay</div>
                  <div>This resident is staying for summer. Knock before entering the room.</div>
                </div>
              )}
              <Check2 label={builtin("builtin.welcome_packs.delivered","Pack delivered").label} v={form.delivered} on={(v) => setForm({ ...form, delivered: v })} testId="wp-delivered" />
              <div>
                <Label>{builtin("builtin.welcome_packs.delivered_on","Delivered on").label}</Label>{questionHelp("builtin.welcome_packs.delivered_on")}
                <input type="date" value={form.delivered_on} data-testid="wp-date" onChange={(e) => setForm({ ...form, delivered_on: e.target.value })} className="w-full border border-gray-300 p-2 text-sm" />
              </div>
              {builtinEnabled("builtin.welcome_packs.note")&&<>{questionHelp("builtin.welcome_packs.note")}<Note label={builtin("builtin.welcome_packs.note","Notes").label} required={builtin("builtin.welcome_packs.note","Notes").required} v={form.note} on={(v) => setForm({ ...form, note: v })} testId="wp-note" /></>}
            </>
          )}
          {(stage === "cleaning_inspection" || stage === "refurbishment_inspection") && (
            <>
              {stage === "cleaning_inspection" && <BathroomCleaningNotice room={room} inspection />}
              {stage === "cleaning_inspection" && <CleaningChecklistSummary room={room} />}
              {(() => {
                const prefix = stage === "cleaning_inspection" ? "ci" : "ri";
                return (
                  <>
                    <Label>{builtin("builtin.cleaning_inspection.result","Result").label}</Label>{questionHelp("builtin.cleaning_inspection.result")}
                    <div className="flex gap-2">
                      {["pass", "fail"].map((r) => (
                        <button
                          key={r}
                          data-testid={`${prefix}-result-${r}`}
                          onClick={() => setForm({ ...form, result: r })}
                          className={`px-3 py-1.5 text-xs font-mono font-bold uppercase border ${
                            form.result === r
                              ? r === "pass"
                                ? "bg-green-600 text-white border-green-600"
                                : "bg-red-600 text-white border-red-600"
                              : "bg-white border-gray-300 hover:border-black"
                          }`}
                        >{r}</button>
                      ))}
                    </div>
                    <Note label="Notes" v={form.notes} on={(v) => setForm({ ...form, notes: v })} testId={`${prefix}-notes`} />
                    {form.result === "fail" && (
                      <div>
                        <Label>Fail reason (required)</Label>
                        <textarea value={form.fail_reason} data-testid={`${prefix}-fail-reason`} onChange={(e) => setForm({ ...form, fail_reason: e.target.value })} className="w-full border border-red-300 p-2 text-sm bg-red-50" />
                      </div>
                    )}
                    <PhotoArrayInput value={form.photos || []} onChange={(v) => setForm({ ...form, photos: v })} testId={`${prefix}-photos`} label="Inspection photos" />
                  </>
                );
              })()}
            </>
          )}
          {stage === "refurbishment" && (
            <>
              {(() => {
                const ri = room.stages.refurbishment_inspection?.form_data;
                if (ri && ri.result === "fail") {
                  return (
                    <div className="border border-red-300 bg-red-50 p-3 text-xs">
                      <div className="font-bold text-red-700 uppercase mb-1">Previous inspection failed</div>
                      <div className="text-red-800 whitespace-pre-wrap">{ri.fail_reason}</div>
                      {ri.photos?.length > 0 && (
                        <div className="flex gap-2 flex-wrap mt-2">
                          {ri.photos.map((p) => <PhotoThumb key={p} path={p} />)}
                        </div>
                      )}
                    </div>
                  );
                }
                return null;
              })()}
              <Note v={form.note} on={(v) => setForm({ ...form, note: v })} testId="refurb-note" />
            </>
          )}
          {stage === "cleaning" && (
            <>
              <BathroomCleaningNotice room={room} />
              {room.bed_size && (
                <div className="border border-gray-200 bg-gray-50 p-2 text-xs font-mono">
                  <div>Bed size: <strong>{room.bed_size}</strong> — place matching mattress protector.</div>
                </div>
              )}
              {(() => {
                const ci = room.stages.cleaning_inspection?.form_data;
                if (ci && ci.result === "fail") {
                  return (
                    <div className="border border-red-300 bg-red-50 p-3 text-xs">
                      <div className="font-bold text-red-700 uppercase mb-1">Previous inspection failed</div>
                      <div className="text-red-800 whitespace-pre-wrap">{ci.fail_reason}</div>
                      {ci.photos?.length > 0 && (
                        <div className="flex gap-2 flex-wrap mt-2">
                          {ci.photos.map((p) => <PhotoThumb key={p} path={p} />)}
                        </div>
                      )}
                    </div>
                  );
                }
                return null;
              })()}
              <YesNa label="Shower curtain changed" v={form.shower_curtain} on={(v) => setForm({ ...form, shower_curtain: v })} testId="cln-shower" />
              <YesNa label="Hoover bag changed" v={form.hoover_bag} on={(v) => setForm({ ...form, hoover_bag: v })} testId="cln-hoover" />
              <YesNa label="Extractor filter changed" v={form.extractor_filter} on={(v) => setForm({ ...form, extractor_filter: v })} testId="cln-extractor" />
              <YesNa label="Mattress protector placed" v={form.mattress_protector} on={(v) => setForm({ ...form, mattress_protector: v })} testId="cln-mattress" />
              {room.bag_and_bin_delegated_to_cleaner && (
                <div className="border border-indigo-200 bg-indigo-50 p-3 space-y-2" data-testid="cln-delegated-bagbin-note">
                  <div className="text-xs text-indigo-900">Bag and bin for this room has been deferred by the site team, please complete the bag and bin as part of the cleaning for this room.</div>
                  {checkoutBagPhoto && <PhotoThumb path={checkoutBagPhoto} />}
                </div>
              )}
              {cleaningRepairOpen && (
                <div className="border border-gray-200 p-3" data-testid="cln-repair-container">
                  <Label>Add repairs from cleaning (optional · internal only)</Label>
                  <div className="space-y-2 mt-1">
                    {(form.repairs_to_add || []).map((it, idx) => (
                      <div key={`clean-repair-${idx}`} className="grid grid-cols-[1fr_28px] gap-2 items-center">
                        <input
                          type="text"
                          value={it.description || ""}
                          onChange={(e) => {
                            const next = [...(form.repairs_to_add || [])];
                            next[idx] = { ...next[idx], description: e.target.value };
                            setForm({ ...form, repairs_to_add: next });
                          }}
                          placeholder="Repair description"
                          className="border border-gray-300 px-2 py-1 text-sm"
                          data-testid={`cln-repair-desc-${idx}`}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const next = (form.repairs_to_add || []).filter((_, i) => i !== idx);
                            setForm({ ...form, repairs_to_add: next.length ? next : [{ description: "" }] });
                          }}
                          className="text-gray-500 hover:text-red-600 text-lg leading-none"
                          aria-label="Remove repair"
                        >×</button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, repairs_to_add: [...(form.repairs_to_add || []), { description: "" }] })}
                      className="px-2 py-1 text-[10px] font-mono font-bold uppercase border border-gray-300 hover:border-black"
                    >
                      + Add another repair
                    </button>
                  </div>
                </div>
              )}
              <Note v={form.note} on={(v) => setForm({ ...form, note: v })} testId="cln-note" />
            </>
          )}
        </div>
    </ResponsiveDialog>
  );
}

function PhotoArrayInput({ value, onChange, testId, label }) {
  return (
    <div data-testid={testId}>
      <Label>{label}</Label>
      <PhotoUploader value={value || []} onChange={onChange} testId={`${testId}-add`} label="" multiple />
    </div>
  );
}

function PhotoThumb({ path, onRemove }) {
  const url = useFileUrl(path);
  if (!url) return null;
  return (
    <div className="relative">
      <a href={url} target="_blank" rel="noreferrer" title="Open full image">
        <img src={url} alt="att" className="h-16 border border-gray-300" />
      </a>
      {onRemove && (
        <button onClick={onRemove} className="absolute top-0 right-0 bg-black text-white p-0.5">
          <X size={10} />
        </button>
      )}
    </div>
  );
}

function YesNa({ label, v, on, testId }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex gap-2">
        {[
          { id: "yes", label: "Yes" },
          { id: "no", label: "No" },
          { id: "na", label: "N/A" },
        ].map((o) => (
          <button
            key={o.id}
            data-testid={`${testId}-${o.id}`}
            onClick={() => on(o.id)}
            className={`px-3 py-1 text-xs font-mono font-bold uppercase border ${
              v === o.id ? "bg-black text-white border-black" : "bg-white border-gray-300 hover:border-black"
            }`}
          >{o.label}</button>
        ))}
      </div>
    </div>
  );
}

function Check2({ label, v, on, testId }) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input type="checkbox" checked={v} onChange={(e) => on(e.target.checked)} data-testid={testId} className="w-4 h-4 accent-black" />
      {label}
    </label>
  );
}

function Note({ label = "Note", v, on, testId, required = false }) {
  return (
    <div>
      <Label>{label}</Label>
      <textarea required={required} value={v || ""} onChange={(e) => on(e.target.value)} data-testid={testId} className="w-full border border-gray-300 p-2 text-sm" rows={2} />
    </div>
  );
}

function Label({ children }) {
  return <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500 mb-1">{children}</div>;
}

function FlagToggle({ label, on, onChange, disabled, testId }) {
  return (
    <button
      onClick={() => !disabled && onChange(!on)}
      disabled={disabled}
      data-testid={testId}
      className={`px-3 py-1.5 text-[11px] font-mono font-bold uppercase tracking-wider border ${
        on ? "bg-black text-white border-black" : "bg-white text-gray-700 border-gray-300"
      } ${disabled ? "opacity-40 cursor-not-allowed" : "hover:border-black"}`}
    >
      {label}: {on ? "On" : "Off"}
    </button>
  );
}

function RepairAddList({ value, onChange, defaultKind = "internal", testIdPrefix = "repair-add", canRemove = true }) {
  const [description, setDescription] = useState("");
  const [kind, setKind] = useState(defaultKind);
  const repairs = value || [];

  const addRepair = () => {
    const trimmed = description.trim();
    if (!trimmed) {
      toast.error("Repair description required");
      return;
    }
    onChange([...repairs, { description: trimmed, kind, is_new: true }]);
    setDescription("");
    setKind(defaultKind);
  };

  const removeRepair = (idx) => onChange(repairs.filter((_, i) => i !== idx));
  const canRemoveRepair = (repair) => (typeof canRemove === "function" ? canRemove(repair) : canRemove);

  return (
    <div className="border border-gray-200 p-3 space-y-3" data-testid={`${testIdPrefix}-panel`}>
      <Label>Add repairs</Label>
      <div className="flex gap-2 flex-wrap">
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Repair description"
          data-testid={`${testIdPrefix}-description`}
          className="flex-1 min-w-[180px] border border-gray-300 px-2 py-1 text-sm"
        />
        <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid={`${testIdPrefix}-kind`} className="border border-gray-300 px-2 py-1 text-sm bg-white">
          <option value="internal">Internal</option>
          <option value="external">External</option>
        </select>
        <button type="button" onClick={addRepair} data-testid={`${testIdPrefix}-add`} className="px-3 py-1 text-[10px] font-mono font-bold uppercase border border-black bg-black text-white">
          Add
        </button>
      </div>
      {repairs.length > 0 && (
        <div className="space-y-2" data-testid={`${testIdPrefix}-list`}>
          {repairs.map((repair, idx) => (
            <div key={`${repair.kind}-${idx}-${repair.description}`} className="flex items-start justify-between gap-2 border border-gray-200 bg-gray-50 px-2 py-1.5">
              <div className="text-xs">
                <span className="font-mono font-bold uppercase text-gray-600">{repair.kind}</span>
                <span className="ml-2">{repair.description}</span>
              </div>
              {canRemoveRepair(repair) && (
                <button type="button" onClick={() => removeRepair(idx)} className="text-gray-500 hover:text-red-600" aria-label="Remove repair">×</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InspectionsTab({ room, onDone, focusStage, lockedStage, canUseStage = () => true }) {
  const { user } = useAuth();
  const { effectiveWorkflow } = useApplication();
  const additionalInspections = (effectiveWorkflow?.stages || []).filter(stage => stage.enabled && stage.is_inspection && !["checkout_inspection","maintenance_inspection"].includes(stage.key) && (!lockedStage || lockedStage === stage.key) && canUseStage(stage.key));
  const [customQuestions, setCustomQuestions] = useState({});
  const [settings, setSettings] = useState(null);
  const showCheckout = (!lockedStage || lockedStage === "checkout_inspection") && canUseStage("checkout_inspection");
  const showMaint = (!lockedStage || lockedStage === "maintenance_inspection") && canUseStage("maintenance_inspection");
  const [co, setCo] = useState({
    vacant: true,
    storage: false,
    damage_items: [],
    bag_and_bin_required: false,
    bag_and_bin_delegated_to_cleaner: false,
    bag_and_bin_photo_path: null,
    notes: "",
  });
  const [mi, setMi] = useState({
    painting_required: false,
    painting_comments: "",
    notes: "",
    repairs_to_add: [],
  });
  useEffect(() => {
    if (room.checkout_inspection) {
      setCo((prev) => ({ ...prev, ...room.checkout_inspection }));
    }
    if (room.maintenance_inspection) {
      const existing = room.maintenance_inspection;
      setMi((prev) => ({
        ...prev,
        ...existing,
        repairs_to_add: [
          ...(existing.internal_repairs || []).map((description) => ({ description, kind: "internal" })),
          ...(existing.external_repairs || []).map((description) => ({ description, kind: "external" })),
        ],
      }));
    }
  }, [room.id, room.checkout_inspection, room.maintenance_inspection]);
  useEffect(() => {
    (async () => {
      const { data } = await api.get("/settings");
      setSettings(data);
      setCustomQuestions(data?.custom_form_questions || {});
    })();
  }, []);

  const submitCo = async () => {
    const isStorageRoom = Boolean(room.flags?.storage);
    if (!isStorageRoom && co.bag_and_bin_required && !co.bag_and_bin_photo_path) {
      toast.error("A photo is required when Bag & Bin is needed");
      return;
    }
    try {
      // Filter out blank damage rows and strip client-only _key field before sending
      const payload = {
        ...co,
        storage: isStorageRoom ? true : co.storage,
        bag_and_bin_required: isStorageRoom ? false : co.bag_and_bin_required,
        bag_and_bin_delegated_to_cleaner: isStorageRoom || isRefurbishmentRoom || !canAdminStaff ? false : co.bag_and_bin_delegated_to_cleaner,
        bag_and_bin_photo_path: isStorageRoom ? null : co.bag_and_bin_photo_path,
        custom_answers: co.custom_answers || {},
        damage_items: (co.damage_items || [])
          .filter((d) => (d.description || "").trim())
          .map(({ _key, ...rest }) => ({
            ...rest,
            amount: Number.isFinite(rest.amount) ? rest.amount : null,
          })),
      };
      await api.post(`/rooms/${room.id}/checkout-inspection`, payload);
      toast.success("Checkout inspection saved");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const submitMi = async () => {
    const repairsToAdd = (mi.repairs_to_add || []).map((r) => ({ description: (r.description || "").trim(), kind: r.kind || "internal" })).filter((r) => r.description);
    await api.post(`/rooms/${room.id}/maintenance-inspection`, {
      painting_required: mi.painting_required,
      painting_comments: mi.painting_comments,
      notes: mi.notes,
      internal_repairs: repairsToAdd.filter((r) => r.kind === "internal").map((r) => r.description),
      external_repairs: repairsToAdd.filter((r) => r.kind === "external").map((r) => r.description),
      custom_answers: mi.custom_answers || {},
    });
    toast.success("Maintenance inspection saved");
    onDone();
  };

  const coStatus = room.stages.checkout_inspection.status;
  const miStatus = room.stages.maintenance_inspection.status;
  const isRefurbishmentRoom = Boolean(room.flags?.refurbishment);
  const isStorageRoom = Boolean(room.flags?.storage);
  const canAdminStaff = (user?.permission_keys||[]).includes("tar.stages.complete_proxy");

  const addDamage = () => setCo({
    ...co,
    damage_items: [
      ...(co.damage_items || []),
      { _key: `dmg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, description: "", amount: "" },
    ],
  });
  const removeDamage = (i) => setCo({ ...co, damage_items: co.damage_items.filter((_, idx) => idx !== i) });
  const updateDamage = (i, patch) => setCo({
    ...co,
    damage_items: co.damage_items.map((d, idx) => (idx === i ? { ...d, ...patch } : d)),
  });

  return (
    <div className="flex flex-col gap-4">
      <RoomFormHeader room={room} settings={settings} />
      {additionalInspections.map(definition=>{const state=room.stages?.[definition.key]||{status:"pending"};const initiallyOpen=focusStage===definition.key||state.status==="in_progress";return <FormCard key={definition.key} title={definition.display_name||definition.name} status={state.status} stage={state} defaultOpen={initiallyOpen} order={definition.display_order||0} testId={`inspection-accordion-${definition.key}`}><SubmissionSummary data={state.form_data||{}} submittedAt={state.completed_at}/><div className="mt-3"><StageActions stage={definition.key} st={state} room={room} onAction={async(stage,action,extra={})=>{try{await api.post(`/rooms/${room.id}/stages/${stage}/action`,{action,...extra});toast.success(`${definition.display_name||definition.name} updated`);onDone();}catch(e){toast.error(e.response?.data?.detail||"Stage action failed");}}} canUseStage={canUseStage}/></div></FormCard>})}
      {showCheckout && (
      <FormCard title="Checkout Inspection" status={coStatus} stage={room.stages.checkout_inspection} defaultOpen={focusStage === "checkout_inspection" || coStatus === "in_progress"} order={(effectiveWorkflow?.stages||[]).findIndex(x=>x.key==="checkout_inspection")} highlight={focusStage === "checkout_inspection"} testId="checkout-inspection-card">
        {coStatus === "in_progress" || coStatus === "completed" ? (
          <div className="space-y-3">
            {!isStorageRoom && (
              <>
                <Check2 label="Items being stored here" v={co.storage} on={(v) => setCo({ ...co, storage: v })} testId="checkout-storage" />
                <Check2 label="Bag & Bin required" v={co.bag_and_bin_required} on={(v) => setCo({ ...co, bag_and_bin_required: v })} testId="checkout-bagbin" />
              </>
            )}
            {!isStorageRoom && co.bag_and_bin_required && (
              <>
                {canAdminStaff && !isRefurbishmentRoom && (
                  <Check2 label="Delegate Bag & Bin to cleaner" v={co.bag_and_bin_delegated_to_cleaner} on={(v) => setCo({ ...co, bag_and_bin_delegated_to_cleaner: v })} testId="checkout-bagbin-delegate" />
                )}
                <PhotoUploader testId="checkout-bagbin-photo" value={co.bag_and_bin_photo_path} onChange={(p) => setCo({ ...co, bag_and_bin_photo_path: p })} label="Bag & Bin photo (required)" />
              </>
            )}
            <div className="border border-gray-200 p-3" data-testid="checkout-damage-items">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] font-mono uppercase tracking-wider text-gray-700 font-bold">Chargeable damages</div>
                <button
                  type="button"
                  onClick={addDamage}
                  data-testid="checkout-damage-add"
                  className="px-2 py-1 text-[10px] font-mono font-bold uppercase border border-gray-300 hover:border-black"
                >
                  + Add line
                </button>
              </div>
              {(co.damage_items || []).length === 0 ? (
                <div className="text-[10px] font-mono text-gray-500">No damages. Click <span className="font-bold">+ Add line</span> to add one charge per damage.</div>
              ) : (
                <div className="space-y-2">
                  {co.damage_items.map((d, i) => (
                    <div key={d._key || `dmg-idx-${i}`} className="grid grid-cols-[1fr_120px_28px] gap-2 items-center" data-testid={`checkout-damage-row-${i}`}>
                      <input
                        type="text"
                        placeholder="Description (e.g. Broken blind)"
                        value={d.description}
                        onChange={(e) => updateDamage(i, { description: e.target.value })}
                        className="border border-gray-300 px-2 py-1 text-sm"
                        data-testid={`checkout-damage-desc-${i}`}
                      />
                      <input
                        type="number"
                        placeholder="Amount £"
                        value={d.amount}
                        onChange={(e) => updateDamage(i, { amount: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                        className="border border-gray-300 px-2 py-1 text-sm font-mono"
                        data-testid={`checkout-damage-amount-${i}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeDamage(i)}
                        data-testid={`checkout-damage-remove-${i}`}
                        className="text-gray-500 hover:text-red-600 text-lg leading-none"
                        aria-label="Remove damage"
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <textarea placeholder="Notes" value={co.notes} onChange={(e) => setCo({ ...co, notes: e.target.value })} className="w-full border border-gray-300 p-2 text-sm" data-testid="checkout-notes" />
            {(customQuestions.checkout_inspection || []).map((q) => (
              <div key={q}>
                <Label>{q}</Label>
                <textarea value={(co.custom_answers || {})[q] || ""} onChange={(e) => setCo({ ...co, custom_answers: { ...(co.custom_answers || {}), [q]: e.target.value } })} className="w-full border border-gray-300 p-2 text-sm" />
              </div>
            ))}
            <button onClick={submitCo} data-testid="checkout-submit" className="bg-black text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider">
              {coStatus === "completed" ? "Update inspection" : "Submit inspection"}
            </button>
          </div>
        ) : (
          <div className="text-sm text-gray-500">Inspection not yet active for this room.</div>
        )}
      </FormCard>
      )}

      {showMaint && (
      <FormCard title="Maintenance Inspection" status={miStatus} stage={room.stages.maintenance_inspection} defaultOpen={focusStage === "maintenance_inspection" || miStatus === "in_progress"} order={(effectiveWorkflow?.stages||[]).findIndex(x=>x.key==="maintenance_inspection")} highlight={focusStage === "maintenance_inspection"} testId="maint-inspection-card">
        {miStatus === "in_progress" || miStatus === "completed" ? (
          <div className="space-y-3">
            <Check2 label="Painting required" v={mi.painting_required} on={(v) => setMi({ ...mi, painting_required: v })} testId="maint-painting" />
            {mi.painting_required && (
              <textarea placeholder="Painting comments (where to paint)" value={mi.painting_comments || ""} onChange={(e) => setMi({ ...mi, painting_comments: e.target.value })} className="w-full border border-gray-300 p-3 text-base" data-testid="maint-painting-comments" rows={4} />
            )}
            <RepairAddList
              value={mi.repairs_to_add || []}
              onChange={(repairs) => setMi({ ...mi, repairs_to_add: repairs })}
              defaultKind="internal"
              testIdPrefix="maint-repair"
              canRemove={(repair) => miStatus !== "completed" || repair.is_new}
            />
            {(customQuestions.maintenance_inspection || []).map((q) => (
              <div key={q}>
                <Label>{q}</Label>
                <textarea value={(mi.custom_answers || {})[q] || ""} onChange={(e) => setMi({ ...mi, custom_answers: { ...(mi.custom_answers || {}), [q]: e.target.value } })} className="w-full border border-gray-300 p-2 text-sm" />
              </div>
            ))}
            <button onClick={submitMi} data-testid="maint-submit" className="bg-black text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider">
              {miStatus === "completed" ? "Update inspection" : "Submit inspection"}
            </button>
          </div>
        ) : (
          <div className="text-sm text-gray-500">Not yet active for this room.</div>
        )}
      </FormCard>
      )}
    </div>
  );
}

function FormCard({ title, status, stage = {}, children, highlight, testId, defaultOpen, order = 0 }) {
  return (
    <details open={defaultOpen} style={{order}} className={`rounded-xl border ${highlight ? "border-black bg-yellow-50" : "border-gray-200 bg-white"}`} data-testid={testId}>
      <summary className="flex min-h-14 cursor-pointer list-none items-center justify-between gap-3 p-4">
        <span><strong className="font-chivo text-base">{title}</strong><span className="ml-2"><StatusPill status={status}/></span>{stage.assignment?.target_label_snapshot&&<small className="ml-2 text-slate-500">{stage.assignment.target_label_snapshot}</small>}{stage.completed_at&&<small className="ml-2 text-slate-500">Latest {new Date(stage.completed_at).toLocaleDateString()}</small>}</span><span aria-hidden="true">⌄</span>
      </summary>
      <div className="border-t p-4">{children}</div>
    </details>
  );
}

function RepairsTab({ room, onDone, lockedStage, canUseStage = () => true }) {
  const { user } = useAuth();
  const repairPermissions=new Set(user?.permission_keys||[]);
  const canUpdateRepair=repairPermissions.has("facilities.repairs.update");
  const canAssignRepair=repairPermissions.has("facilities.repairs.assign");
  const canCompleteRepair=repairPermissions.has("facilities.repairs.complete");
  const canEscalateRepair=repairPermissions.has("facilities.repairs.escalate");
  const canReopenRepair=repairPermissions.has("facilities.repairs.reopen");
  const canDeleteRepair=repairPermissions.has("facilities.repairs.delete");
  const canInternal = canUseStage("internal_repairs");
  const canExternal = canUseStage("external_repairs");
  const defaultKind = lockedStage === "external_repairs" || (!canInternal && canExternal) ? "external" : "internal";
  const [desc, setDesc] = useState("");
  const [kind, setKind] = useState(defaultKind);
  const [filter, setFilter] = useState(
    lockedStage === "internal_repairs" ? "internal"
      : lockedStage === "external_repairs" ? "external"
      : "all"
  );
  const [raiseFor, setRaiseFor] = useState(null);
  const [completeFor, setCompleteFor] = useState(null);
  const [assignFor,setAssignFor]=useState(null);

  const add = async () => {
    if ((kind === "internal" && !canInternal) || (kind === "external" && !canExternal)) return;
    if (!desc.trim()) return;
    await api.post(`/rooms/${room.id}/repairs`, { description: desc, kind });
    setDesc("");
    onDone();
  };

  const act = async (id, action, hold_reason, comment) => {
    await api.patch(`/repairs/${id}`, { action, hold_reason, comment });
    onDone();
  };

  const deleteRepair = async (repair) => {
    if (!canDeleteRepair) return;
    if (!window.confirm(`Delete this repair?\n\n${repair.description}`)) return;
    await api.delete(`/repairs/${repair.id}`);
    toast.success("Repair deleted");
    onDone();
  };

  const accessibleKinds = [canInternal && "internal", canExternal && "external"].filter(Boolean);
  const repairs = (room.repairs || []).filter((r) => accessibleKinds.includes(r.kind) && (filter === "all" || r.kind === filter));
  const all = (room.repairs || []).filter((r) => accessibleKinds.includes(r.kind));
  const counts = {
    internal: all.filter((r) => r.kind === "internal").length,
    external: all.filter((r) => r.kind === "external").length,
    open: all.filter((r) => r.status === "pending").length,
    on_hold: all.filter((r) => r.status === "on_hold").length,
    raised: all.filter((r) => r.status === "raised").length,
    completed: all.filter((r) => r.status === "completed").length,
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-0 border border-gray-200 bg-white" data-testid="repair-counts">
        <CountTile label="Open" value={counts.open} />
        <CountTile label="On hold" value={counts.on_hold} alert={counts.on_hold > 0} />
        <CountTile label="Raised" value={counts.raised} accent />
        <CountTile label="Completed" value={counts.completed} good />
      </div>

      <div className="border border-gray-200 p-4">
        <div className="font-chivo font-black text-base mb-3">Add repair</div>
        <div className="flex gap-2">
          <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Description" data-testid="repair-desc-input" className="flex-1 border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-black" />
          <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="repair-kind-select" className="border border-gray-300 px-3 py-2 text-sm bg-white">
            {canInternal && <option value="internal">Internal</option>}
            {canExternal && <option value="external">External</option>}
          </select>
          <button onClick={add} data-testid="repair-add-button" className="bg-black text-white px-3 py-2 text-xs font-semibold uppercase">Add</button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[
          { id: "all", label: `All (${counts.internal + counts.external})` },
          canInternal && { id: "internal", label: `Internal (${counts.internal})` },
          canExternal && { id: "external", label: `External (${counts.external})` },
        ].filter(Boolean).map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`repair-filter-${f.id}`}
            className={`px-3 py-1.5 text-[11px] font-mono font-bold uppercase border ${
              filter === f.id ? "bg-black text-white border-black" : "bg-white border-gray-300 hover:border-black"
            }`}
          >{f.label}</button>
        ))}
      </div>

      <div className="border border-gray-200 bg-white">
        {repairs.length === 0 && (
          <div className="p-4 text-sm text-gray-500">No repairs in this filter.</div>
        )}
        {repairs.map((r) => {
          const actions=[
            r.kind==="internal"&&canAssignRepair&&{label:r.assignment?"Reassign":"Assign Job",onSelect:()=>setAssignFor(assignFor===r.id?null:r.id)},
            r.status==="on_hold"&&canUpdateRepair&&{label:"Resume",onSelect:()=>act(r.id,"resume")},
            !["completed","on_hold"].includes(r.status)&&canUpdateRepair&&{label:"Put On Hold",onSelect:()=>{const reason=prompt("Hold reason");if(reason)act(r.id,"hold",reason)}},
            r.status!=="completed"&&canCompleteRepair&&!(r.kind==="external"&&r.status==="raised")&&{label:"Complete",onSelect:()=>{const comment=prompt("Completion comment (optional)");act(r.id,"complete",undefined,comment||"")}},
            r.kind==="external"&&r.status==="raised"&&canCompleteRepair&&{label:"Complete contractor repair",onSelect:()=>setCompleteFor(r)},
            r.status!=="completed"&&canUpdateRepair&&{label:r.is_duplicate?"Unmark Duplicate":"Mark Duplicate",onSelect:()=>act(r.id,r.is_duplicate?"unmark_duplicate":"mark_duplicate")},
            r.kind==="internal"&&r.status!=="completed"&&canEscalateRepair&&{label:"Move to External",onSelect:()=>act(r.id,"escalate")},
            r.kind==="external"&&r.status!=="completed"&&canUpdateRepair&&canReopenRepair&&{label:"Move to Internal",onSelect:()=>act(r.id,"deescalate")},
            r.kind==="external"&&r.status!=="completed"&&canAssignRepair&&{label:r.status==="raised"?"Edit contractor details":"Assign Contractor",onSelect:()=>setRaiseFor(r)},
            r.status==="completed"&&canReopenRepair&&{label:"Reopen",onSelect:()=>act(r.id,"reopen")},
            canUpdateRepair&&{label:"Add Comment",onSelect:()=>{const comment=prompt("Add repair comment");if(comment?.trim())act(r.id,"add_comment",undefined,comment.trim())}},
            canDeleteRepair&&{label:"Delete",danger:true,onSelect:()=>deleteRepair(r)},
          ].filter(Boolean);
          return <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`repair-row-${r.id}`}>
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-mono font-bold uppercase tracking-wider border border-gray-300 px-1">{r.kind}</span>
                <StatusPill status={r.status} />
                {r.is_duplicate && (
                  <span className="text-[10px] font-mono font-bold uppercase tracking-wider border border-amber-300 bg-amber-50 text-amber-700 px-1">Duplicate</span>
                )}
                {r.job_number && (
                  <span className="text-[10px] font-mono text-gray-700">Job {r.job_number}</span>
                )}
                {r.expected_date && (
                  <span className="text-[10px] font-mono text-gray-700">ETA {r.expected_date}</span>
                )}
              </div>
              <div className="text-base leading-relaxed mt-1">{r.description}</div>
              <dl className="mt-3 grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">{r.kind==="internal"&&<RepairDatum label="Assignment" value={r.assignment?.target_label_snapshot||"Unassigned"}/>} {r.kind==="external"&&<RepairDatum label="Contractor" value={r.contractor_name||"Not assigned"}/>}<RepairDatum label="Created" value={r.created_at?new Date(r.created_at).toLocaleDateString():"Not recorded"}/><RepairDatum label="Completed" value={r.completed_at?new Date(r.completed_at).toLocaleDateString():"Not completed"}/></dl>
              {r.hold_reason && (<div className="text-[11px] font-mono text-gray-500 mt-1">Hold: {r.hold_reason}</div>)}
              {r.completion_notes && (<div className="text-[11px] font-mono text-green-700 mt-1">Done: {r.completion_notes}</div>)}
              {(r.comments || []).length > 0 && (
                <div className="mt-2 border border-gray-200 bg-gray-50 p-2 space-y-1">
                  {(r.comments || []).map((c, idx) => (
                    <div key={`${r.id}-c-${idx}`} className="text-[11px] font-mono text-gray-700">
                      <span className="text-gray-500">{new Date(c.at).toLocaleString()} · {c.by}:</span> {c.text}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="mt-3 flex justify-end"><OperationalActionMenu actions={actions} label="Actions" ariaLabel={`Actions for ${r.description || r.id}`}/></div>
            {assignFor===r.id&&r.kind==="internal"&&<div className="mt-3 rounded-lg border bg-slate-50 p-3"><h4 className="mb-2 font-bold">{r.assignment?"Reassign Job":"Assign Job"}</h4><AssignmentPicker optionsUrl={`/repairs/${r.id}/assignment-options`} saveUrl={`/repairs/${r.id}/assignment`} assignment={r.assignment} onSaved={()=>{setAssignFor(null);onDone()}}/></div>}

          </div>;
        })}
      </div>

      {raiseFor && <RaiseDialog repair={raiseFor} onClose={() => setRaiseFor(null)} onDone={onDone} />}
      {completeFor && <CompleteCxDialog repair={completeFor} onClose={() => setCompleteFor(null)} onDone={onDone} />}
    </div>
  );
}

function pickCountTileColor(good, alert, accent) {
  if (good) return "text-green-700";
  if (alert) return "text-amber-700";
  if (accent) return "text-purple-700";
  return "text-black";
}

function RepairDatum({label,value}) { return <div><dt className="text-[9px] font-bold uppercase tracking-wide text-slate-400">{label}</dt><dd className="mt-0.5 font-semibold text-slate-700">{value}</dd></div>; }

function CountTile({ label, value, accent, good, alert }) {
  return (
    <div className="border-r border-b border-gray-200 p-3">
      <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`font-chivo font-black text-2xl mt-1 ${pickCountTileColor(good, alert, accent)}`}>{value}</div>
    </div>
  );
}

function RaiseDialog({ repair, onClose, onDone }) {
  const [contractor,setContractor]=useState(repair.contractor_name||"");
  const [job, setJob] = useState(repair.job_number||"");
  const [date, setDate] = useState(repair.expected_date||"");
  const [note, setNote] = useState(repair.note||"");

  const submit = async () => {
    if (!job.trim()) { toast.error("Job number required"); return; }
    await api.post(`/repairs/${repair.id}/raise`, { contractor_name:contractor.trim()||null, job_number: job, expected_date: date || null, note });
    toast.success("Repair raised");
    onClose();
    onDone();
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white border border-gray-300 w-full max-w-md">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="font-chivo font-black text-lg">Raise with contractor</div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-base leading-relaxed text-gray-700">{repair.description}</div>
          <div><Label>Contractor</Label><input value={contractor} onChange={e=>setContractor(e.target.value)} data-testid="raise-contractor" className="w-full border border-gray-300 px-3 py-2 text-sm" placeholder="Contractor or supplier name"/></div>
          <div>
            <Label>Job number</Label>
            <input value={job} onChange={(e) => setJob(e.target.value)} data-testid="raise-job-number" className="w-full border border-gray-300 px-3 py-2 text-sm" />
          </div>
          <div>
            <Label>Expected attendance date</Label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="raise-date" className="w-full border border-gray-300 px-3 py-2 text-sm" />
          </div>
          <div>
            <Label>Note (optional)</Label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="raise-note" className="w-full border border-gray-300 p-2 text-sm" />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs font-semibold uppercase tracking-wider border border-gray-300">Cancel</button>
          <button onClick={submit} data-testid="raise-submit" className="bg-black text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider">Save</button>
        </div>
      </div>
    </div>
  );
}

function CompleteCxDialog({ repair, onClose, onDone }) {
  const [notes, setNotes] = useState("");

  const submit = async () => {
    await api.post(`/repairs/${repair.id}/complete-cx`, { completion_notes: notes });
    toast.success("Marked completed");
    onClose();
    onDone();
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white border border-gray-300 w-full max-w-md">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="font-chivo font-black text-lg">Mark contractor-completed</div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-base leading-relaxed text-gray-700">{repair.description}</div>
          <div>
            <Label>Completion notes</Label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="cx-notes" className="w-full border border-gray-300 p-3 text-base" rows={4} />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs font-semibold uppercase tracking-wider border border-gray-300">Cancel</button>
          <button onClick={submit} data-testid="cx-submit" className="bg-black text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider">Save</button>
        </div>
      </div>
    </div>
  );
}

function AuditTab({ roomId, audit, onAudit }) {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [busy, setBusy] = useState(false);
  const filter = async () => {
    setBusy(true);
    try { const { data } = await api.get(`/rooms/${roomId}/audit`, { params: { limit: 100, date_from: dateFrom || undefined, date_to: dateTo || undefined } }); onAudit(data); }
    catch (error) { toast.error(error.response?.data?.detail || "Could not load Room activity"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <form className="grid gap-3 rounded-lg border bg-slate-50 p-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end" onSubmit={event => { event.preventDefault(); filter(); }}>
        <label className="text-xs font-semibold">From<input aria-label="Audit from date" type="date" value={dateFrom} onChange={event => setDateFrom(event.target.value)} className="mt-1 block w-full rounded border bg-white p-2 text-sm" /></label>
        <label className="text-xs font-semibold">To<input aria-label="Audit to date" type="date" value={dateTo} onChange={event => setDateTo(event.target.value)} className="mt-1 block w-full rounded border bg-white p-2 text-sm" /></label>
        <button disabled={busy} className="rounded bg-black px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{busy ? "Loading…" : "Apply dates"}</button>
      </form>
      <div className="border border-gray-200 bg-white" data-testid="audit-list">
      {audit.length === 0 && <div className="p-4 text-sm text-gray-500">No activity yet.</div>}
      {audit.map((a) => (
        <div key={a.id} className="px-4 py-3 border-b border-gray-100 last:border-b-0">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-semibold">{friendlyAuditAction(a.action)}</div>
              {friendlyAuditDetail(a.detail) && <div className="text-xs text-gray-600 mt-0.5">{friendlyAuditDetail(a.detail)}</div>}
              <div className="text-[10px] font-mono text-gray-400 uppercase mt-1">{a.user_email || "Team member"}</div>
            </div>
            <div className="text-[10px] font-mono text-gray-400 whitespace-nowrap">
              {new Date(a.at).toLocaleString()}
            </div>
          </div>
        </div>
      ))}
      </div>
    </div>
  );
}

function friendlyAuditAction(action="Activity updated") {
  return String(action).replace(/^Configured flag (apply|remove)d$/i,(_,operation)=>operation.toLowerCase()==="apply"?"Added a Room flag":"Removed a Room flag").replaceAll("_"," ").replace(/^./,c=>c.toUpperCase());
}
function friendlyAuditDetail(detail) {
  if(!detail)return "";
  try { const value=JSON.parse(detail);if(value.event==="assignment.changed"){const before=value.previous?.target_label_snapshot||"Unassigned",after=value.current?.target_label_snapshot||"Unassigned";return `Changed assignment from ${before} to ${after}${value.reason?` · ${value.reason}`:""}`;}if(value.label)return value.label;return "Updated operational information"; } catch { return detail; }
}
